import React, { useState } from "react";
import { FaDownload } from "react-icons/fa";
import QRCode from "qrcode";
import { SecondaryButton } from "../../UI/DesignStandards";
import { SITE_ORIGIN } from "../../SEO/siteOrigin";
import { tournamentPath } from "../../../utility/tournamentUrls";

const QR_SIZE = 400;
const LOGO_SIZE_RATIO = 0.22; // Logo takes ~22% of QR size so it still scans (high error correction)
const LOGO_PADDING_RATIO = 0.04; // White padding around logo

const QRCodeGenerator = ({ tournamentId, tournamentName, logoUrl }) => {
  const [qrCodeType, setQrCodeType] = useState("standings");
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  const drawLogoInCenter = (ctx, logoImage) => {
    const pad = QR_SIZE * LOGO_PADDING_RATIO;
    const boxSize = QR_SIZE * LOGO_SIZE_RATIO + pad * 2;
    const x = (QR_SIZE - boxSize) / 2;
    const y = (QR_SIZE - boxSize) / 2;
    const logoSize = boxSize - pad * 2;
    const logoX = x + pad;
    const logoY = y + pad;

    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    const r = 8;
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + boxSize - r, y);
    ctx.quadraticCurveTo(x + boxSize, y, x + boxSize, y + r);
    ctx.lineTo(x + boxSize, y + boxSize - r);
    ctx.quadraticCurveTo(
      x + boxSize,
      y + boxSize,
      x + boxSize - r,
      y + boxSize,
    );
    ctx.lineTo(x + r, y + boxSize);
    ctx.quadraticCurveTo(x, y + boxSize, x, y + boxSize - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.fill();

    ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
  };

  const loadLogoImage = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });

  const generateQRCode = async () => {
    try {
      setIsGeneratingQR(true);

      let url;
      let filename;

      switch (qrCodeType) {
        case "overview":
          url = `${SITE_ORIGIN}${tournamentPath(tournamentId)}`;
          filename = `${tournamentName || "tournament"}-overview-qr.png`;
          break;
        case "home":
          url = SITE_ORIGIN;
          filename = "bowlingtournamentapp-home-qr.png";
          break;
        case "signup":
          url = `${SITE_ORIGIN}${tournamentPath(tournamentId, "/signups")}`;
          filename = `${tournamentName || "tournament"}-signup-qr.png`;
          break;
        case "standings":
        default:
          url = `${SITE_ORIGIN}${tournamentPath(tournamentId, "/standings")}`;
          filename = `${tournamentName || "tournament"}-standings-qr.png`;
          break;
      }

      const canvas = document.createElement("canvas");
      canvas.width = QR_SIZE;
      canvas.height = QR_SIZE;
      const ctx = canvas.getContext("2d");

      await QRCode.toCanvas(canvas, url, {
        width: QR_SIZE,
        margin: 2,
        errorCorrectionLevel: "H",
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      const logoSrc =
        logoUrl || (process.env.PUBLIC_URL || "") + "/favicon.ico";
      const logoImage = await loadLogoImage(logoSrc);
      if (logoImage) {
        drawLogoInCenter(ctx, logoImage);
      }

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
          "image/png",
          1,
        );
      });
      const downloadUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error generating QR code:", error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  return (
    <div className="card-board p-6">
      <h2 className="heading-display text-lg mb-1">QR Codes</h2>
      <p className="text-sm text-brand-muted mb-4">
        Download a code to print on flyers.
      </p>

      <div className="flex items-center gap-4">
        <select
          value={qrCodeType}
          onChange={(e) => setQrCodeType(e.target.value)}
          className="flex-1 min-w-0 px-4 py-2 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
        >
          <option value="standings">Standings</option>
          <option value="overview">Overview</option>
          <option value="home">Home Page</option>
          <option value="signup">Sign Up Page</option>
        </select>

        <SecondaryButton
          onClick={generateQRCode}
          disabled={isGeneratingQR}
          className="flex-shrink-0 !px-3 !py-2 text-sm whitespace-nowrap"
        >
          <FaDownload className="mr-2" />
          {isGeneratingQR ? "Generating..." : "Download"}
        </SecondaryButton>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
